import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Globe, Search, BarChart3, Megaphone, CheckCircle } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";

export default function WebMarketingPage() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  usePageSeo({
    title: "Web & Digital Marketing | LAB336 – Online-Sichtbarkeit für KMU",
    description: "Professionelles Web-Design und Digital Marketing für Schweizer KMU. Website-Entwicklung, SEO, Content-Strategie und Online-Werbung — alles aus einer Hand.",
    canonical: `${BASE_URL}/web-marketing`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "name": "Web & Digital Marketing",
          "description": "Website-Entwicklung und Digital Marketing für Schweizer KMU",
          "provider": { "@type": "Organization", "name": "LAB336", "url": BASE_URL },
          "areaServed": { "@type": "Country", "name": "Schweiz" },
          "serviceType": "Digital Marketing"
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
            { "@type": "ListItem", "position": 2, "name": "Leistungen", "item": `${BASE_URL}/#services` },
            { "@type": "ListItem", "position": 3, "name": "Web & Digital Marketing", "item": `${BASE_URL}/web-marketing` }
          ]
        }
      ]
    }
  });

  const goToScheduler = () => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector("#scheduler");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="service-page-web-marketing">
      <header className="bg-lab-primary text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-playfair text-xl tracking-wide">LAB<span className="text-lab-accent">336</span></Link>
          <Link to="/" className="text-sm text-white/80 hover:text-white flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Startseite
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-lab-bg-light py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 bg-white flex items-center justify-center mb-6">
              <Globe className="w-8 h-8 text-lab-accent" strokeWidth={1.5} />
            </div>
            <span className="text-lab-accent text-sm font-semibold tracking-widest uppercase">Leistung</span>
            <h1 className="font-playfair text-3xl lg:text-5xl font-semibold text-lab-primary mt-4 max-w-3xl">
              Web & Digital Marketing für Schweizer KMU
            </h1>
            <p className="text-lab-text-muted text-lg mt-6 max-w-2xl leading-relaxed">
              Ihre Website ist Ihr wichtigster Verkäufer — wenn sie richtig aufgestellt ist. Wir sorgen dafür, dass Sie online gefunden werden und Besucher zu Kunden machen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Nutzenargumentation */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-6">Warum Ihre Online-Präsenz über Erfolg oder Misserfolg entscheidet</h2>
            <div className="text-lab-text-muted leading-relaxed space-y-4">
              <p>
                Die meisten Ihrer potenziellen Kunden informieren sich online, bevor sie einen Anbieter kontaktieren. Wenn Ihre Website veraltet wirkt, auf dem Handy schlecht funktioniert oder bei Google nicht auftaucht, verlieren Sie Aufträge — ohne es überhaupt zu merken. Das gilt besonders für lokale Schweizer KMU, die in ihrer Region sichtbar sein müssen.
              </p>
              <p>
                Das Problem vieler Unternehmens-Websites: Sie wurden einmal erstellt und dann sich selbst überlassen. Die Inhalte sind veraltet, die Technik ist langsam und es gibt keinen klaren Weg für Besucher, eine Anfrage zu stellen oder Kontakt aufzunehmen. Eine schöne Website allein reicht nicht — sie muss gefunden werden und Besucher überzeugen.
              </p>
              <p>
                Wir kombinieren Website-Entwicklung mit digitaler Strategie. Das heisst: Ihre neue Website sieht nicht nur professionell aus, sondern ist technisch auf dem neuesten Stand, für Suchmaschinen optimiert und klar auf Ihre Geschäftsziele ausgerichtet. Jede Seite hat einen Zweck, jeder Besucher einen klaren nächsten Schritt.
              </p>
              <p>
                Und weil eine Website kein einmaliges Projekt ist, begleiten wir Sie auch danach: mit regelmässigen Inhalten, SEO-Optimierung und Auswertung der Besucherdaten. So wird Ihre Online-Präsenz zu einem Kanal, der kontinuierlich neue Kunden bringt — nicht nur eine digitale Visitenkarte.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-lab-bg-light py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-4">Beispiele aus der Praxis</h2>
            <p className="text-lab-text-muted mb-10">Was eine professionelle Online-Präsenz bewirken kann:</p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Architekturbüro mit 6 Mitarbeitenden",
                  problem: "Veraltete Website ohne mobile Version. Keine Anfragen über die Website, alle Aufträge kamen über persönliche Kontakte.",
                  solution: "Moderne Portfolio-Website mit Projektgalerie, SEO-optimierten Leistungsseiten und Kontaktformular.",
                  result: "4-5 qualifizierte Anfragen pro Monat über die Website."
                },
                {
                  title: "Zahnarztpraxis mit 3 Standorten",
                  problem: "Bei Google auf Seite 3, Patienten fanden die Praxis nur über Empfehlung.",
                  solution: "Lokale SEO-Strategie, Google Business Profile Optimierung und neue Website mit Online-Terminbuchung.",
                  result: "Platz 1-3 für alle relevanten lokalen Suchbegriffe, 60% mehr Neupatienten."
                },
                {
                  title: "B2B-Dienstleister mit 20 Mitarbeitenden",
                  problem: "Website hatte viel Traffic, aber kaum Conversions. Besucher verliessen die Seite nach wenigen Sekunden.",
                  solution: "Conversion-optimiertes Redesign mit klarer Nutzenargumentation, Fallstudien und Lead-Magneten.",
                  result: "Conversion-Rate von 0.8% auf 4.2% gesteigert."
                },
              ].map((item) => (
                <div key={item.title} className="bg-white border border-lab-border p-6">
                  <h3 className="font-playfair text-lg font-semibold text-lab-primary mb-3">{item.title}</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-lab-text-muted"><span className="font-semibold text-lab-primary">Problem:</span> {item.problem}</p>
                    <p className="text-lab-text-muted"><span className="font-semibold text-lab-primary">Lösung:</span> {item.solution}</p>
                    <p className="text-lab-accent font-medium"><CheckCircle className="w-4 h-4 inline mr-1" />{item.result}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-8">Unsere Leistungen im Detail</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: Globe, title: "Website-Entwicklung", text: "Moderne, schnelle Websites, die auf jedem Gerät perfekt funktionieren. Mit klarer Struktur, überzeugenden Inhalten und technisch einwandfrei — damit Google und Ihre Besucher zufrieden sind." },
              { icon: Search, title: "SEO & Suchmaschinenoptimierung", text: "Gefunden werden, wenn es zählt. Wir optimieren Ihre Website für die Suchbegriffe, die Ihre Zielkunden verwenden — mit technischer SEO, Content-Optimierung und lokaler Sichtbarkeit." },
              { icon: BarChart3, title: "Analytics & Conversion-Optimierung", text: "Verstehen, was Besucher auf Ihrer Website tun — und daraus lernen. Wir richten professionelles Tracking ein und optimieren kontinuierlich, damit mehr Besucher zu Kunden werden." },
              { icon: Megaphone, title: "Content-Strategie", text: "Inhalte, die Ihre Kompetenz zeigen und Vertrauen aufbauen. Von der Blog-Strategie über Fallstudien bis zu LinkedIn-Beiträgen — abgestimmt auf Ihre Zielgruppe und Ihre Branche." },
            ].map((item) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                className="bg-lab-bg-light p-8 border border-lab-border">
                <item.icon className="w-6 h-6 text-lab-accent mb-4" strokeWidth={1.5} />
                <h3 className="font-playfair text-xl font-semibold text-lab-primary mb-3">{item.title}</h3>
                <p className="text-lab-text-muted leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vorgehen */}
      <section className="bg-lab-bg-dark py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-12">Unser Vorgehen</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Analyse", text: "Ihre Zielgruppe verstehen, Wettbewerb analysieren und klare Ziele definieren — bevor wir mit Design und Technik starten." },
              { step: "02", title: "Konzept & Design", text: "Struktur, Inhalte und visuelles Design entwickeln. Sie sehen und testen alles, bevor die Entwicklung beginnt." },
              { step: "03", title: "Umsetzung", text: "Website technisch umsetzen, SEO-Grundlagen implementieren und Tracking einrichten. Launch mit Qualitätssicherung." },
              { step: "04", title: "Wachstum", text: "Laufende SEO-Optimierung, Content-Erstellung und Performance-Analyse. Ihre Online-Präsenz wird kontinuierlich stärker." },
            ].map((item, i) => (
              <motion.div key={item.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}
                className="text-white">
                <span className="text-lab-accent text-4xl font-playfair font-semibold">{item.step}</span>
                <h3 className="font-playfair text-xl font-semibold mt-4 mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-lab-primary py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-4">
            Wie gut ist Ihre Online-Präsenz wirklich?
          </h2>
          <p className="text-gray-300 mb-4 leading-relaxed">
            In einem kostenlosen 30-Minuten-Gespräch prüfen wir gemeinsam, wie Ihre Website bei Google abschneidet, wo Besucher abspringen und welche Quick Wins sofort mehr Anfragen bringen können.
          </p>
          <p className="text-lab-accent font-medium mb-8">
            Inkl. kurzer SEO-Analyse Ihrer aktuellen Website. Unverbindlich und ehrlich.
          </p>
          <button onClick={goToScheduler} className="bg-lab-accent text-lab-primary font-semibold px-10 py-4 text-base inline-flex items-center gap-2 hover:bg-white transition-colors" data-testid="cta-scheduler">
            Kostenlosen Website-Check buchen <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
}
